import { isBefore, isEqual, isWithinInterval, subDays } from 'date-fns';
import { useMemo } from 'react';
import { GlPeriodObject } from 'src/services/useGLPeriodApi';
import { removeTime } from 'src/utils/formatTime';
import { GLPeriodCalendarYear } from 'src/utils/transformation';
import { flattenMessages } from '../view_checkbook';

export interface PeriodLineItemError {
  endDate: string[];
}

export interface PeriodLineItemWarning {
  startDate: string[];
  endDate: string[];
  generic: string[];
}

export interface PeriodLineItemInfo {}

const defaultViewBudgetErrors: PeriodLineItemError = {
  endDate: [],
};

const defaultViewBudgetWarnings: PeriodLineItemWarning = {
  startDate: [],
  endDate: [],
  generic: [],
};

export const defaultViewBudgetInfos: PeriodLineItemInfo = {};

const usePeriodErrors = (
  items: GLPeriodCalendarYear,
  originalFiscalYearPeriod: GLPeriodCalendarYear[],
  currentPeriod: GlPeriodObject | null,
  isEditMode: boolean
) => {
  const errors = useMemo(() => {
    const { periods = [], yearEndDateObj, yearStartDateObj, isCurrYear } = items || {};
    return periods?.map((item, index) => {
      let error: PeriodLineItemError = { ...defaultViewBudgetErrors };
      const { startDate, endDate, glPeriodId } = item;
      const nextPeriod = periods[index + 1];
      const currentDate = removeTime(new Date());
      const hasPassed = isBefore(endDate, currentDate);
      const isCurrentPeriod =
        isCurrYear && (currentPeriod?.glPeriodId === glPeriodId || item.isNewPeriod);
      const isLastFiscalYear =
        originalFiscalYearPeriod[originalFiscalYearPeriod.length - 1].fiscalYear ===
        items.fiscalYear;

      if (isEditMode) {
        if (!endDate) {
          error.endDate = ['Please enter an end date'];
        } else if (
          !isLastFiscalYear &&
          !isWithinInterval(endDate, { start: yearStartDateObj, end: yearEndDateObj })
        ) {
          error.endDate = ['End Date must be in Fiscal Year'];
        } else if (isCurrentPeriod && hasPassed) {
          error.endDate = ['End Date must be a future date'];
        } else if (startDate && endDate) {
          if (isBefore(endDate, startDate) || isEqual(endDate, startDate)) {
            error.endDate = ['Start/End Dates must not overlap with other periods'];
          }
        } else if (index !== periods.length - 1 && endDate && nextPeriod.startDate) {
          if (!isEqual(endDate, subDays(nextPeriod.startDate, 1))) {
            error.endDate = ['There must not be a gap between period Start/End Dates'];
          }
        }
      }
      return error;
    }, []);
  }, [currentPeriod?.glPeriodId, isEditMode, items, originalFiscalYearPeriod]);

  const warnings = useMemo(() => {
    const { periods = [], fiscalYear } = items || {};
    const originalPeriodList = originalFiscalYearPeriod.find(
      (item) => item.fiscalYear === fiscalYear
    ) as GLPeriodCalendarYear;

    return periods?.map((item) => {
      let warning: PeriodLineItemWarning = { ...defaultViewBudgetWarnings };

      if (isEditMode) {
        const originalPeriod = originalPeriodList?.periods?.find(
          (period) => period.rowId === item.rowId
        );
        if (originalPeriod) {
          if (!isEqual(item.startDate, originalPeriod.startDate)) {
            warning.startDate = ['Start date has changed'];
          }
          if (!isEqual(item.endDate, originalPeriod.endDate)) {
            warning.endDate = ['End date has changed'];
          }
        }
        if (item.isNewPeriod) {
          warning.generic = ['Newly added period'];
        }
      }

      return warning;
    });
  }, [isEditMode, items, originalFiscalYearPeriod]);

  const infos = useMemo(() => {
    const { periods = [] } = items || {};

    return periods?.map(() => {
      let messages: PeriodLineItemInfo = { ...defaultViewBudgetInfos };
      return messages;
    });
  }, [items]);

  const periodErrorList: string[] = flattenMessages(errors);
  const periodWarningList: string[] = flattenMessages(warnings);
  const periodInformationList: string[] = flattenMessages(infos);

  const hasPeriodErrors = useMemo(() => !!periodErrorList.length, [periodErrorList.length]);
  const hasPeriodWarnings = useMemo(() => !!periodWarningList.length, [periodWarningList.length]);

  return {
    errors,
    warnings,
    infos,
    periodErrorList,
    periodInformationList,
    periodWarningList,
    hasPeriodErrors,
    hasPeriodWarnings,
  };
};

export default usePeriodErrors;
